import { gatewayApi as api } from './gatewayApi';

// Types
export interface PackingList {
  id?: string;
  PackingListNum: string;
  PackingListName: string;
  Number: string; // Customer PO
  RevDate: string;
  Active: boolean;
  
  // Shipping Information
  ShipFrom: string;
  ShipFromCo: string;
  ShipFromSt: string;
  ShipFromCity: string;
  ShipFromState?: string;
  ShipFromZIP?: string;
  ShipFromPhone?: string;
  ShipFromEmail?: string;
  
  ShipTo: string;
  ShipToCo: string;
  ShipToSt: string;
  ShipToCity: string;
  ShipToState: string;
  ShipToZIP: string;
  ShipToPhone: string;
  ShipToEmail: string;
  
  BillTo?: string;
  BillToCo?: string;
  BillToSt?: string;
  BillToCity?: string;
  BillToState?: string;
  BillToZIP?: string;
  BillToPhone?: string;
  BillToEmail?: string;
  
  // Export Information
  CountryOrigin: string;
  USSchedBCode: string;
  ECCN: string;
  ExportLicense: string;
  TypeContainer: string;
  
  // Measurements
  NetWeight: number;
  GrossWeight: number;
  Volume: number;
  TotalShippedQty: number;
  TotalContainer: number;
  
  // Reference
  RefDocName: string;
  
  // Dates
  CreatedDate?: string;
  ModifiedDate?: string;
  
  // Related entities
  ShipToLocation?: ShipToLocation;
  PackingListDetails?: PackingListDetail[];
}

export interface PackingListDetail {
  id?: string;
  PackingListId: string;
  ProductId: string;
  Quantity: number;
  Description: string;
  UnitPrice?: number;
  TotalPrice?: number;
  Active: boolean;
  
  // Related entities
  Product?: {
    id: string;
    Number: string;
    Description: string;
    UnitOfMeasure?: string;
  };
}

export interface ShipToLocation {
  id: string;
  AttName: string;
  AttSt: string;
  AttCity: string;
  AttState?: string;
  AttZIP?: string;
  AttPhone?: string;
  AttEmail?: string;
  Active: boolean;
}

export interface Product {
  id: string;
  Number: string;
  Description: string;
  UnitOfMeasure?: string;
  Active: boolean;
}

export interface CreatePackingListRequest {
  PackingListNum: string;
  PackingListName: string;
  Number: string; // Customer PO
  
  // Shipping Information
  ShipFrom: string;
  ShipFromCo: string;
  ShipFromSt: string;
  ShipFromCity: string;
  ShipTo: string;
  ShipToCo: string;
  ShipToSt: string;
  ShipToCity: string;
  ShipToState: string;
  ShipToZIP: string;
  ShipToPhone: string;
  ShipToEmail: string;
  
  // Export Information
  CountryOrigin: string;
  USSchedBCode: string;
  ECCN: string;
  ExportLicense: string;
  TypeContainer: string;
  
  // Measurements
  NetWeight: number;
  GrossWeight: number;
  Volume: number;
  TotalShippedQty: number;
  TotalContainer: number;
  
  RefDocName: string;
}

export interface SearchPackingListsRequest {
  pageNum?: number;
  pageSize?: number;
  sort?: string;
  filter?: {
    Active?: boolean;
    PackingListNum?: string;
    PackingListName?: string;
    [key: string]: any;
  };
}

export interface SearchPackingListsResponse {
  data: PackingList[];
  totalCount: number;
  pageNum: number;
  pageSize: number;
}

export interface PackingListReportData {
  packingList: PackingList;
  details: PackingListDetail[];
  totalWeight: number;
  totalVolume: number;
  totalValue: number;
}

// Packing List API Service
export const packingListApi = {
  // Get all packing lists with optional filtering
  getAll: (params?: SearchPackingListsRequest): Promise<SearchPackingListsResponse> =>
    api.post('/packinglist/search', params),

  // Get packing list by ID
  getById: (id: string): Promise<PackingList> =>
    api.get(`/packinglist/${id}`),

  // Create new packing list
  create: (data: CreatePackingListRequest): Promise<PackingList> =>
    api.post('/packinglist/', data),

  // Update packing list
  update: (data: Partial<PackingList>): Promise<PackingList> =>
    api.put('/packinglist/', data),

  // Delete packing list
  delete: (id: string): Promise<void> =>
    api.delete(`/packinglist/${id}`),

  // Search packing lists
  search: (field: string, value: string): Promise<PackingList[]> =>
    api.post('/packinglist/search', { field, value }),

  // Packing List Details Management
  getDetails: (packingListId: string): Promise<PackingListDetail[]> =>
    api.post('/packinglistdetail/search', { filter: { PackingListId: packingListId } }),

  addDetail: (packingListId: string, detail: Omit<PackingListDetail, 'id' | 'PackingListId'>): Promise<PackingListDetail> =>
    api.post('/packinglistdetail/', { ...detail, PackingListId: packingListId }),

  updateDetail: (detailData: PackingListDetail): Promise<PackingListDetail> =>
    api.put('/packinglistdetail/', detailData),

  deleteDetail: (detailId: string): Promise<void> =>
    api.delete(`/packinglistdetail/${detailId}`),

  // Get Dropdown Data
  getShipToLocations: (): Promise<ShipToLocation[]> =>
    api.get('/shipto/'),

  getProducts: (): Promise<Product[]> =>
    api.get('/product/'),

  // Report Generation
  generateReport: (packingListId: string): Promise<PackingListReportData> =>
    api.get(`/reports/packinglist/${packingListId}`),

  exportToPdf: (packingListId: string): Promise<Blob> =>
    api.getBlob(`/packinglist/${packingListId}/export`),

  // Utility methods
  validatePackingList: (packingList: Partial<PackingList>): string[] => {
    const errors: string[] = [];
    
    if (!packingList.PackingListNum?.trim()) {
      errors.push('Packing List Number is required');
    }
    
    if (!packingList.PackingListName?.trim()) {
      errors.push('Packing List Name is required');
    }
    
    if (!packingList.Number?.trim()) {
      errors.push('Customer PO Number is required');
    }
    
    // Shipping validation
    if (!packingList.ShipFrom?.trim()) {
      errors.push('Ship From Name is required');
    }
    
    if (!packingList.ShipTo?.trim()) {
      errors.push('Ship To Name is required');
    }
    
    if (!packingList.ShipToEmail?.trim() || !packingList.ShipToEmail.includes('@')) {
      errors.push('Valid Ship To Email is required');
    }
    
    // Export information validation
    if (!packingList.CountryOrigin?.trim()) {
      errors.push('Country of Origin is required');
    }
    
    // Numeric validations
    if (packingList.NetWeight !== undefined && packingList.NetWeight < 0) {
      errors.push('Net Weight cannot be negative');
    }
    
    if (packingList.GrossWeight !== undefined && packingList.GrossWeight < 0) {
      errors.push('Gross Weight cannot be negative');
    }
    
    if (packingList.Volume !== undefined && packingList.Volume < 0) {
      errors.push('Volume cannot be negative');
    }
    
    if (packingList.TotalShippedQty !== undefined && packingList.TotalShippedQty < 0) {
      errors.push('Total Shipped Quantity cannot be negative');
    }
    
    return errors;
  },

  calculateTotals: (details: PackingListDetail[]): {
    totalQuantity: number;
    totalValue: number;
    itemCount: number;
  } => {
    return details.reduce(
      (totals, detail) => ({
        totalQuantity: totals.totalQuantity + detail.Quantity,
        totalValue: totals.totalValue + (detail.TotalPrice || 0),
        itemCount: totals.itemCount + 1,
      }),
      { totalQuantity: 0, totalValue: 0, itemCount: 0 }
    );
  },

  // Shipping address formatting
  formatShippingAddress: (
    name: string,
    company: string,
    street: string,
    city: string,
    state?: string,
    zip?: string
  ): string => {
    const parts = [
      name,
      company,
      street,
      [city, state].filter(Boolean).join(', '),
      zip
    ].filter(Boolean);
    
    return parts.join('\n');
  },

  // Weight conversion utilities
  convertWeight: (weight: number, fromUnit: 'lb' | 'kg', toUnit: 'lb' | 'kg'): number => {
    if (fromUnit === toUnit) return weight;
    
    if (fromUnit === 'lb' && toUnit === 'kg') {
      return weight * 0.453592;
    } else if (fromUnit === 'kg' && toUnit === 'lb') {
      return weight * 2.20462;
    }
    
    return weight;
  },

  // Volume conversion utilities
  convertVolume: (volume: number, fromUnit: 'lts' | 'gal', toUnit: 'lts' | 'gal'): number => {
    if (fromUnit === toUnit) return volume;
    
    if (fromUnit === 'lts' && toUnit === 'gal') {
      return volume * 0.264172;
    } else if (fromUnit === 'gal' && toUnit === 'lts') {
      return volume * 3.78541;
    }
    
    return volume;
  },
}; 